import {
  ArrowUpRight,
  Brain,
  Compass,
  FileText,
  LineChart,
  ListChecks,
  PencilLine,
  Sparkles,
  Timer,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { BrainSpot } from "@/platform/ui/illustrations/spot";

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";

interface Phase {
  icon: LucideIcon;
  kicker: string;
  title: string;
  body: string;
  deliverables: string[];
  tone: Tone;
}

const phases: Phase[] = [
  {
    icon: Compass,
    kicker: "Fase 1",
    title: "Diagnóstico calibrado",
    body: "Cerca de 2 horas. Mini-prova objetiva equilibrada por disciplina, uma redação curta sob a rubrica Vunesp, duas discursivas e uma leitura cronometrada. Resultado vira vetor de proficiência calibrado por Elo.",
    deliverables: [
      "Foto inicial com 10 habilidades nomeadas",
      "Nota bruta projetada + padronizada estimada",
      "3 focos prioritários com ganho marginal estimado",
    ],
    tone: "violet",
  },
  {
    icon: ListChecks,
    kicker: "Fase 2",
    title: "Plano adaptativo",
    body: "Cada bloco é decidido por: peso na prova × lacuna individual × probabilidade de recorrência × curva de esquecimento × tempo até a prova. Interleaving entre disciplinas; 1/3 novo, 2/3 revisão.",
    deliverables: [
      "Plano semanal entre 6h e 16h, ajustado à sua agenda",
      "Bloco diário com itens sequenciados",
      "Sistema de revisão espaçada (SM-2) integrado",
    ],
    tone: "pink",
  },
  {
    icon: PencilLine,
    kicker: "Fase 3",
    title: "Redação Vunesp",
    body: "Correção sob a rubrica oficial em 4 critérios (tema, estrutura, língua, coesão). Detector de modelo pronto, cópia da coletânea e repertório desconectado. Plano de reescrita por critério.",
    deliverables: [
      "Nota 0–20 com decomposição por critério",
      "Feedback focado em autonomia textual",
      "Reescrita iterativa com checkpoints",
    ],
    tone: "yellow",
  },
  {
    icon: FileText,
    kicker: "Fase 4",
    title: "Discursivas",
    body: "Biologia, Química, Física, Matemática e Inglês com rubricas próprias. Discursiva ≠ dissertação: respostas técnicas curtas são recompensadas. Sempre mostra mínima e excelente.",
    deliverables: [
      "Rubrica calibrada por disciplina",
      "Resposta mínima e exemplar lado a lado",
      "Erros conceituais mapeados para o plano",
    ],
    tone: "mint",
  },
  {
    icon: Timer,
    kicker: "Fase 5",
    title: "Simulado completo",
    body: "Cinco horas no formato Einstein. 50 objetivas distribuídas por disciplina, 5 discursivas e 1 redação Vunesp. Timer rigoroso, sem pausas — fidelidade ao dia da prova.",
    deliverables: [
      "Nota padronizada estimada (média 500, DP 100)",
      "Heatmap de tempo por questão",
      "Posição relativa simulada na coorte",
    ],
    tone: "coral",
  },
  {
    icon: LineChart,
    kicker: "Fase 6",
    title: "Dashboard de aprovação",
    body: "Tudo conectado: nota projetada, evolução por área, risco por disciplina, simulado projetado vs. real. Honesto: não inventa números.",
    deliverables: [
      "North star: ganho semanal mediano",
      "Funil pessoal: diagnóstico → semana 1 → semana 4",
      "Relatório de risco para revisão semanal",
    ],
    tone: "sky",
  },
];

const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

const TONE_DOT: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent)]",
  pink: "bg-[color:var(--color-pink)]",
  mint: "bg-[color:var(--color-mint)]",
  yellow: "bg-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral)]",
  sky: "bg-[color:var(--color-sky)]",
};

export function MethodPage() {
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 top-96 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="pink"
        className="pointer-events-none absolute right-10 top-20 w-14 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-20 md:py-28">
        {/* HERO */}
        <header className="animate-fade-up mx-auto max-w-3xl text-center">
          <Badge variant="accentSolid" className="mx-auto w-fit">
            <Sparkles className="size-3" aria-hidden />
            Método em 6 fases
          </Badge>
          <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
            Engenharia reversa da banca,{" "}
            <span className="text-gradient-accent">calibrada por psicometria.</span>
          </h1>
          <p className="mt-5 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
            Cada fase é mensurável e auditável. Você vê o que move a nota — e o que ainda não move.
          </p>
        </header>

        {/* FASES */}
        <div className="mt-16 grid gap-5 md:grid-cols-2">
          {phases.map(({ icon: Icon, kicker, title, body, deliverables, tone }, i) => (
            <article
              key={title}
              className="group lift relative overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 md:p-8 shadow-[var(--shadow-sm)] animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start justify-between">
                <span className={`chip size-12 ${TONE_BG[tone]}`}>
                  <Icon className="size-5" aria-hidden />
                </span>
                <Badge variant="outline">{kicker}</Badge>
              </div>
              <h2 className="mt-6 font-display text-[length:var(--text-h2)] font-semibold leading-[var(--text-h2--line-height)] tracking-[var(--text-h2--letter-spacing)]">
                {title}
              </h2>
              <p className="mt-3 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] text-pretty">
                {body}
              </p>
              <ul className="mt-6 space-y-2.5 border-t border-[--color-border] pt-5 text-[length:var(--text-body-sm)]">
                {deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3">
                    <span aria-hidden className={`mt-1.5 inline-block size-2 rounded-full shrink-0 ${TONE_DOT[tone]}`} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {/* CONEXÃO */}
        <div className="mt-16 relative overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-8 md:p-12 shadow-[var(--shadow-md)]">
          <Blob
            color="violet"
            className="pointer-events-none absolute -right-20 -top-20 w-72 opacity-25 blur-2xl"
          />
          <div className="relative grid gap-8 md:grid-cols-[auto_1fr] md:items-start md:gap-12">
            <BrainSpot className="size-24" />
            <div>
              <Badge variant="accent">Motor adaptativo</Badge>
              <h3 className="mt-3 font-display text-[length:var(--text-h1)] font-semibold leading-[var(--text-h1--line-height)] tracking-[var(--text-h1--letter-spacing)]">
                Como tudo se conecta na decisão semanal.
              </h3>
              <p className="mt-3 max-w-2xl text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)] text-pretty">
                O motor recalcula a cada noite. Você acorda com um plano novo, e cada item já tem o
                porquê na frente.
              </p>

              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <ConnectionList
                  label="Entradas"
                  tone="pink"
                  items={[
                    "Vetor de proficiência (Elo por skill)",
                    "Peso oficial da skill na prova",
                    "Recência da última prática",
                    "Confiança autorrelatada",
                    "Dias restantes até a prova",
                  ]}
                />
                <ConnectionList
                  label="Saída"
                  tone="mint"
                  items={[
                    "Lista priorizada de blocos (60–90 min)",
                    "Tipo de prática (nova, revisão, simulada)",
                    "Ganho marginal estimado por bloco",
                    "Justificativa em uma frase por escolha",
                  ]}
                />
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <Button asChild size="xl" variant="gradient">
            <Link to="/cadastro">
              Começar diagnóstico
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </Button>
          <Button asChild size="xl" variant="outline">
            <Link to="/precos">Ver planos</Link>
          </Button>
        </div>
      </Container>
    </div>
  );
}

function ConnectionList({
  label,
  tone,
  items,
}: {
  label: string;
  tone: Tone;
  items: string[];
}) {
  return (
    <div>
      <Badge variant={tone === "violet" ? "accent" : (tone as "pink" | "mint" | "yellow" | "coral" | "sky")}>
        {label}
      </Badge>
      <ul className="mt-3 space-y-2 text-[length:var(--text-body-sm)]">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-3">
            <span aria-hidden className={`mt-1.5 inline-block size-1.5 rounded-full shrink-0 ${TONE_DOT[tone]}`} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// suppress unused — keeping the import for future use
void Brain;
